import { useEffect, useMemo, useState, type CSSProperties, type ReactElement } from "react";
import { Mail, Save, UserRound, X } from "lucide-react";
import { Badge, Button, FormField, Select, Stack, Surface, Text, TextInput } from "@ux/primitives";
import { UserPicker } from "@ux/compositions";
import { palette, radius, space, typography } from "@ux/tokens";
import type { AssetAssignmentView } from "../../admin/asset-assignments/types";
import type { UserDirectoryEntry } from "../../admin/users/types";
import { formatUserDisplay, formatUserShortName } from "../../admin/users/display";
import { useUserDirectoryEntry } from "../../admin/users/hooks";
import {
    floorplanSpaceTitle,
    workspaceTypes,
    type WorkspaceUpsertPayload,
    type WorkspaceView,
} from "../../admin/workspaces/types";
import { WorkspaceAssetsPanel } from "../../admin/workspaces/WorkspaceAssetsPanel";

export interface DeskProfilePanelProps {
    readonly open: boolean;
    readonly elementId: string | null;
    readonly workspace: WorkspaceView | null;
    readonly occupantUserId: string | null;
    readonly canEdit: boolean;
    readonly isBusy: boolean;
    readonly errorMessage: string | null;
    readonly onClose: () => void;
    readonly onCreateWorkspace: () => void;
    readonly onSaveWorkspace: (payload: WorkspaceUpsertPayload) => void;
    readonly onAssignOccupant: (userId: string) => void;
    readonly onVacate: () => void;
    readonly onAddNewAsset: () => void;
    readonly onAssignExistingAsset: () => void;
    readonly onReleaseAssetAssignment: (assignment: AssetAssignmentView) => void;
    readonly onStartInventory?: () => void;
    readonly onReassignAll?: () => void;
}

export function DeskProfilePanel({
    open,
    elementId,
    workspace,
    occupantUserId,
    canEdit,
    isBusy,
    errorMessage,
    onClose,
    onCreateWorkspace,
    onSaveWorkspace,
    onAssignOccupant,
    onVacate,
    onAddNewAsset,
    onAssignExistingAsset,
    onReleaseAssetAssignment,
    onStartInventory,
    onReassignAll,
}: DeskProfilePanelProps): ReactElement {
    const occupantQuery = useUserDirectoryEntry(occupantUserId);
    const occupant = occupantQuery.data ?? null;
    const occupied = occupantUserId !== null && occupantUserId !== "";
    const [editingWorkspace, setEditingWorkspace] = useState(false);

    useEffect(() => {
        setEditingWorkspace(false);
    }, [workspace?.id, elementId, open]);

    return (
        <aside
            style={{
                ...drawerStyle,
                width: open ? "min(380px, 92vw)" : 0,
                borderRightColor: open ? palette.border : "transparent",
            }}
            aria-hidden={!open}
        >
            <div style={innerStyle}>
                <div style={headerStyle}>
                    <Stack gap={1} grow>
                        <Text role="eyebrow">
                            {occupied
                                ? (workspace?.type ?? "Space")
                                : `${workspace?.type ?? "Space"} · ${elementId ?? ""}`}
                        </Text>
                        <Text role="heading" as="h2">
                            {floorplanSpaceTitle(
                                occupied ? formatUserShortName(occupant) : null,
                                workspace?.friendlyName,
                                workspace?.name?.trim() || elementId || "Desk",
                            )}
                        </Text>
                    </Stack>
                    <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Close profile"
                        onClick={onClose}
                        leadingIcon={<X size={16} />}
                    >
                        Close
                    </Button>
                </div>

                <div style={bodyStyle}>
                    {!elementId ? (
                        <Text role="muted">Select a desk or room on the map.</Text>
                    ) : workspace === null ? (
                        <UnmappedSpace
                            elementId={elementId}
                            canEdit={canEdit}
                            isBusy={isBusy}
                            onCreateWorkspace={onCreateWorkspace}
                        />
                    ) : (
                        <Stack gap={4}>
                            {editingWorkspace ? (
                                <WorkspaceDetailsEditor
                                    workspace={workspace}
                                    canEdit={canEdit}
                                    isBusy={isBusy}
                                    onSave={(payload) => {
                                        onSaveWorkspace(payload);
                                        setEditingWorkspace(false);
                                    }}
                                    onCancel={() => setEditingWorkspace(false)}
                                />
                            ) : (
                                <WorkspaceDetailsSummary
                                    workspace={workspace}
                                    canEdit={canEdit}
                                    onEdit={() => setEditingWorkspace(true)}
                                />
                            )}

                            <OccupantBlock
                                occupied={occupied}
                                occupant={occupant}
                                occupantUserId={occupantUserId}
                                canEdit={canEdit}
                                isBusy={isBusy}
                                onAssign={onAssignOccupant}
                                onVacate={onVacate}
                            />

                            {errorMessage !== null ? (
                                <Text role="muted" color={palette.danger}>
                                    {errorMessage}
                                </Text>
                            ) : null}

                            <WorkspaceAssetsPanel
                                workspaceId={workspace.id}
                                onAddNewAsset={onAddNewAsset}
                                onAssignExistingAsset={onAssignExistingAsset}
                                onReleaseAssignment={onReleaseAssetAssignment}
                                {...(onStartInventory !== undefined
                                    ? { onStartInventory }
                                    : {})}
                                {...(onReassignAll !== undefined ? { onReassignAll } : {})}
                                disabled={!canEdit || workspace.isDeleted}
                            />
                        </Stack>
                    )}
                </div>
            </div>
        </aside>
    );
}

function WorkspaceDetailsSummary({
    workspace,
    canEdit,
    onEdit,
}: {
    readonly workspace: WorkspaceView;
    readonly canEdit: boolean;
    readonly onEdit: () => void;
}): ReactElement {
    return (
        <Surface tone="raised" padding={3}>
            <Stack gap={3}>
                <Stack direction="row" align="center" justify="space-between" gap={2} wrap>
                    <Text role="eyebrow">Workspace</Text>
                    {canEdit && !workspace.isDeleted ? (
                        <Button variant="secondary" size="sm" onClick={onEdit}>
                            Edit
                        </Button>
                    ) : null}
                </Stack>
                <DetailRow label="Workspace ID" value={workspace.id} />
                <DetailRow label="Name" value={workspace.name} />
                <DetailRow label="Type" value={workspace.type} />
                <DetailRow
                    label="Friendly name"
                    value={workspace.friendlyName?.trim() || "—"}
                />
                <DetailRow
                    label="Floorplan element id"
                    value={workspace.floorplanElementId?.trim() || "—"}
                />
            </Stack>
        </Surface>
    );
}

function DetailRow({
    label,
    value,
}: {
    readonly label: string;
    readonly value: string;
}): ReactElement {
    return (
        <div>
            <Text role="eyebrow">{label}</Text>
            <Text role="body" style={{ marginTop: space[1] }}>
                {value}
            </Text>
        </div>
    );
}

function WorkspaceDetailsEditor({
    workspace,
    canEdit,
    isBusy,
    onSave,
    onCancel,
}: {
    readonly workspace: WorkspaceView;
    readonly canEdit: boolean;
    readonly isBusy: boolean;
    readonly onSave: (payload: WorkspaceUpsertPayload) => void;
    readonly onCancel: () => void;
}): ReactElement {
    const [name, setName] = useState(workspace.name);
    const [type, setType] = useState(workspace.type);
    const [friendlyName, setFriendlyName] = useState(workspace.friendlyName ?? "");
    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
        setName(workspace.name);
        setType(workspace.type);
        setFriendlyName(workspace.friendlyName ?? "");
        setValidationError(null);
    }, [workspace]);

    const dirty = useMemo(() => {
        const nextFriendly = friendlyName.trim();
        return (
            name.trim() !== workspace.name ||
            type !== workspace.type ||
            nextFriendly !== (workspace.friendlyName ?? "").trim()
        );
    }, [name, type, friendlyName, workspace]);

    const readOnly = !canEdit || workspace.isDeleted;

    function handleSave(): void {
        const trimmedName = name.trim();
        if (trimmedName === "") {
            setValidationError("Name is required.");
            return;
        }
        if (type === "") {
            setValidationError("Pick a workspace type.");
            return;
        }
        const trimmedFriendly = friendlyName.trim();
        setValidationError(null);
        onSave({
            id: workspace.id,
            sageOfficeId: workspace.sageOfficeId,
            type,
            name: trimmedName,
            friendlyName: trimmedFriendly === "" ? null : trimmedFriendly,
            // Map placement uses workspace name (= SVG group id).
            floorplanElementId: workspace.floorplanElementId?.trim() || null,
        });
    }

    return (
        <Surface tone="raised" padding={3}>
            <Stack gap={3}>
                <Text role="eyebrow">Edit workspace</Text>
                <DetailRow label="Workspace ID" value={workspace.id} />
                <FormField label="Name" required>
                    {(id) => (
                        <TextInput
                            id={id}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={readOnly || isBusy}
                        />
                    )}
                </FormField>
                <FormField label="Type" required>
                    {(id) => (
                        <Select
                            id={id}
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                            disabled={readOnly || isBusy}
                            options={workspaceTypes.map((t) => ({ value: t, label: t }))}
                        />
                    )}
                </FormField>
                <FormField label="Friendly name" hint="Optional alias shown on the map and lists.">
                    {(id) => (
                        <TextInput
                            id={id}
                            value={friendlyName}
                            onChange={(e) => setFriendlyName(e.target.value)}
                            disabled={readOnly || isBusy}
                            placeholder="e.g. Near kitchen"
                        />
                    )}
                </FormField>
                <DetailRow
                    label="Floorplan element id"
                    value={workspace.floorplanElementId?.trim() || "—"}
                />
                {validationError !== null ? (
                    <Text role="muted" color={palette.danger}>
                        {validationError}
                    </Text>
                ) : null}
                <Stack direction="row" gap={2} wrap>
                    <Button
                        variant="primary"
                        size="sm"
                        disabled={isBusy || !dirty || readOnly}
                        leadingIcon={<Save size={14} />}
                        onClick={handleSave}
                    >
                        Save workspace
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        disabled={isBusy}
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>
                </Stack>
            </Stack>
        </Surface>
    );
}

function UnmappedSpace({
    elementId,
    canEdit,
    isBusy,
    onCreateWorkspace,
}: {
    readonly elementId: string;
    readonly canEdit: boolean;
    readonly isBusy: boolean;
    readonly onCreateWorkspace: () => void;
}): ReactElement {
    return (
        <Stack gap={3}>
            <Badge tone="warn">Not linked</Badge>
            <Text role="muted">
                No workspace is mapped to {elementId} yet.
                {canEdit
                    ? " Create a desk workspace to assign people and equipment."
                    : " Ask an editor to create the workspace from this map shape."}
            </Text>
            {canEdit ? (
                <Button
                    variant="primary"
                    size="sm"
                    disabled={isBusy}
                    onClick={onCreateWorkspace}
                >
                    Create desk workspace
                </Button>
            ) : null}
        </Stack>
    );
}

function OccupantBlock({
    occupied,
    occupant,
    occupantUserId,
    canEdit,
    isBusy,
    onAssign,
    onVacate,
}: {
    readonly occupied: boolean;
    readonly occupant: UserDirectoryEntry | null;
    readonly occupantUserId: string | null;
    readonly canEdit: boolean;
    readonly isBusy: boolean;
    readonly onAssign: (userId: string) => void;
    readonly onVacate: () => void;
}): ReactElement {
    const selected = useMemo((): UserDirectoryEntry | null => {
        if (!occupied || occupantUserId === null) return null;
        return (
            occupant ?? {
                id: occupantUserId,
                displayName: null,
                email: null,
                userPrincipalName: null,
            }
        );
    }, [occupied, occupant, occupantUserId]);

    return (
        <Stack gap={3}>
            <Stack direction="row" align="center" gap={2}>
                <Text role="eyebrow">Occupant</Text>
                <Badge tone={occupied ? "accent" : "ok"}>
                    {occupied ? "Occupied" : "Vacant"}
                </Badge>
            </Stack>

                            <Avatar
                                {...(occupant?.displayName
                                    ? { name: occupant.displayName }
                                    : {})}
                            />

            {occupied && occupantUserId !== null ? (
                <Text role="muted">User ID {occupantUserId}</Text>
            ) : null}

            {canEdit ? (
                <Stack gap={2}>
                    <UserPicker
                        value={selected}
                        disabled={isBusy}
                        onChange={(user) => {
                            if (user === null) {
                                if (occupied) onVacate();
                                return;
                            }
                            onAssign(user.id);
                        }}
                        placeholder="Search HR directory…"
                    />
                    {occupied ? (
                        <Button
                            variant="secondary"
                            size="sm"
                            disabled={isBusy}
                            onClick={onVacate}
                        >
                            Vacate desk
                        </Button>
                    ) : null}
                </Stack>
            ) : occupied && occupantUserId !== null ? (
                <Stack gap={2}>
                    <Text role="body">
                        {formatUserDisplay(occupant, occupantUserId)}
                    </Text>
                    {occupant?.email ? (
                        <div style={fieldValueRow}>
                            <Mail size={14} />
                            <Text role="muted">{occupant.email}</Text>
                        </div>
                    ) : null}
                </Stack>
            ) : (
                <Text role="muted">No current occupant</Text>
            )}
        </Stack>
    );
}

function Avatar({ name }: { readonly name?: string }): ReactElement {
    const initials = (name ?? "")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase() ?? "")
        .join("");

    return (
        <div style={avatarStyle}>
            {initials ? (
                <span style={avatarInitialsStyle}>{initials}</span>
            ) : (
                <UserRound size={28} color={palette.fgSubtle} />
            )}
        </div>
    );
}

const drawerStyle: CSSProperties = {
    flexShrink: 0,
    overflow: "hidden",
    transition: "width 220ms ease",
    background: palette.surface,
    borderRight: `1px solid transparent`,
    display: "flex",
    flexDirection: "column",
    zIndex: 6,
};

const innerStyle: CSSProperties = {
    width: "min(380px, 92vw)",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
};

const headerStyle: CSSProperties = {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: space[2],
    padding: `${space[4]} ${space[4]} ${space[3]}`,
    borderBottom: `1px solid ${palette.border}`,
};

const bodyStyle: CSSProperties = {
    padding: space[4],
    overflowY: "auto",
    flex: 1,
};

const avatarStyle: CSSProperties = {
    width: 72,
    height: 72,
    borderRadius: radius.lg,
    border: `1px solid ${palette.border}`,
    background: palette.surfaceInset,
    display: "grid",
    placeItems: "center",
    overflow: "hidden",
};

const avatarInitialsStyle: CSSProperties = {
    fontFamily: typography.family.body,
    fontSize: typography.size.xl,
    fontWeight: typography.weight.semibold,
    color: palette.accentFg,
};

const fieldValueRow: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: space[2],
    color: palette.fgMuted,
};
